import React, { useState } from 'react';
import classNames from 'classnames';

import { FleetRuleBody } from './components/fleet-rule-body';
import { FleetRuleHeader } from './components/fleet-rule-header';
import { FleetRuleFooter } from './components/fleet-rule-footer';
import { Condition, getEmptyCondition } from '../rule/rule-utils';
import {
	CommonFleetRuleProps,
	FleetRuleTranslations,
	FleetRuleLeftSideHandlers,
	FleetRuleLeftSideOptions,
	FleetRuleRightSideOptions,
	FleetRuleHeaderOptions,
	FleetRuleHeaderHandlers,
	FleetRuleFooterOptions,
	FleetRuleFooterHandlers,
	FeatureFlags
} from './fleet-rule-utils';
import { findAttribute, toggleAttrOptions } from '../rule/rule-utils';
import { useAttrOptionsState } from '../hooks';

import { DEFAULT_PANEL_KEY } from '../../../components/collapse/collapse';
import { Collapse } from 'components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { FleetRuleRightSideHandlers } from './fleet-rule-utils';

import styles from './fleet-rule.module.scss';
import './fleet-rule.scss';

export interface FleetRuleProps extends CommonFleetRuleProps {
	translations: FleetRuleTranslations;
	fleetRuleRightSideHandlers: FleetRuleRightSideHandlers;
	fleetRuleRightSideOptions: FleetRuleRightSideOptions;
	fleetRuleLeftSideOptions: FleetRuleLeftSideOptions;
	fleetRuleLeftSideHandlers: FleetRuleLeftSideHandlers;
	fleetRuleHeaderOptions: FleetRuleHeaderOptions;
	fleetRuleHeaderHandlers: FleetRuleHeaderHandlers;
	fleetRuleFooterOptions: FleetRuleFooterOptions;
	fleetRuleFooterHandlers: FleetRuleFooterHandlers;
	isOpen?: boolean;
	toggleOpen?(isOpen: boolean): void;
	handleChangeConditions(conditions: Condition[]): void;
	toggleEnabled: (isEnabled: boolean) => void;
	setHoveredParent: (enabled: boolean) => void;
	featureFlags?: FeatureFlags;
}

const FleetRule = ({
	translations,
	disabled = false,
	isOpen = false,
	fleetRuleRightSideHandlers,
	fleetRuleRightSideOptions,
	fleetRuleLeftSideOptions,
	fleetRuleLeftSideHandlers,
	fleetRuleHeaderOptions,
	fleetRuleHeaderHandlers,
	fleetRuleFooterOptions,
	fleetRuleFooterHandlers,
	toggleOpen,
	setHoveredParent,
	handleChangeConditions,
	toggleEnabled,
	featureFlags
}: FleetRuleProps) => {
	const [attrOptions, setAttrOptions] = useAttrOptionsState(
		fleetRuleLeftSideOptions.conditions,
		fleetRuleLeftSideOptions.attributes
	);
	const [isHovered, setIsHovered] = useState(false);

	const handleHovered = value => {
		setIsHovered(value);
		setHoveredParent(value);
	};

	const handleChange = (condition, value) => {
		const mappedConditions = fleetRuleLeftSideOptions.conditions.map(cond =>
			cond.id === condition.id
				? {
						...condition,
						value
				  }
				: cond
		);
		handleChangeConditions(mappedConditions);
	};

	const removeCondition = (condition: Condition) => {
		handleChangeConditions(fleetRuleLeftSideOptions.conditions.filter(cond => cond.id !== condition.id));
		const attribute = findAttribute(attrOptions, condition.path);
		if (!attribute) return;
		setAttrOptions(toggleAttrOptions(attr => attr.path === attribute.path, { disabled: false }, attrOptions));
	};

	const addCondition = (key: string) => {
		const attribute = findAttribute(attrOptions, key);
		if (!attribute) return;
		const { type, options, path, display_path } = attribute;
		if (!type || !display_path) return;
		const newCondition = getEmptyCondition({
			name: display_path,
			index: fleetRuleLeftSideOptions.conditions.length,
			path,
			type,
			options,
			onChange: handleChange,
			onRemove: removeCondition
		});
		handleChangeConditions([...fleetRuleLeftSideOptions.conditions, newCondition]);
	};

	const toggleSwitch = (checked: boolean, e) => {
		e.stopPropagation();
		toggleEnabled(checked);
	};

	const isCollapsable = !!toggleOpen;
	const headerClasses = classNames('fleet-router-rule-header', {
		collapsable: isCollapsable
	});

	const header = (
		<FleetRuleHeader
			isHovered={isHovered}
			handleHovered={handleHovered}
			className={headerClasses}
			disabled={disabled}
			inactiveText={translations.inactive}
			toggleSwitch={toggleEnabled ? toggleSwitch : undefined}
			fleetRuleHeaderOptions={fleetRuleHeaderOptions}
			fleetRuleHeaderHandlers={fleetRuleHeaderHandlers}
		/>
	);

	return (
		<div data-test-id="fleet-router-rule" className={styles.main}>
			<React.Fragment>
				<Collapse
					expandIconPosition="right"
					className="fleet-router-rule-container"
					expandIcon={({ isActive }) =>
						isActive ? (
							<BringgIcon iconName={BringgFontIcons.Chevron} />
						) : (
							<BringgIcon iconName={BringgFontIcons.ChevronUp} />
						)
					}
					defaultActiveKey={isOpen ? DEFAULT_PANEL_KEY : ''}
					onChange={toggleOpen}
				>
					<Collapse.Panel key={DEFAULT_PANEL_KEY} header={header} showArrow={isCollapsable}>
						<div className={styles['body-container']}>
							<FleetRuleBody
								translations={translations.body}
								disabled={disabled}
								addCondition={addCondition}
								handleChange={handleChange}
								removeCondition={removeCondition}
								fleetRuleRightSideOptions={fleetRuleRightSideOptions}
								fleetRuleRightSideHandlers={fleetRuleRightSideHandlers}
								fleetRuleLeftSideOptions={fleetRuleLeftSideOptions}
								fleetRuleLeftSideHandlers={fleetRuleLeftSideHandlers}
								featureFlags={featureFlags}
							/>
							<FleetRuleFooter
								disabled={disabled}
								fleetRuleFooterOptions={fleetRuleFooterOptions}
								fleetRuleFooterHandlers={fleetRuleFooterHandlers}
								translations={translations.footer}
							/>
						</div>
					</Collapse.Panel>
				</Collapse>
			</React.Fragment>
		</div>
	);
};

export default FleetRule;
